import Icon, { IconType } from 'components/Icon';
import FrameLink from 'components/Widget/FrameLink';
import { Tooltip } from 'components/Widget/Tooltip';
import React, { useMemo, useState } from 'react';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import remarkGfm from 'remark-gfm';
import { selectIsMobile } from 'redux/mode/reducers';
import { RootReducer } from 'redux/reducers';
import * as S from './styles';

interface MarkdownTextProps {
	children: string;
	frameIds?: Set<string>;
	onContextMenu?: (e: React.MouseEvent) => void;
}

const CodeBlock = React.memo(({ children }: { children: string }) => {
	const [isCollapsed, setIsCollapsed] = useState(true);
	const [tooltipId] = useState(String(Math.random()));
	const lineCount = (children?.match(/\n/g) || []).length + 1;
	const hasOverflow = lineCount > 5;

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(children);
			toast.success('Copied to clipboard');
		} catch (error) {
			toast.error('Failed to copy code');
		}
	};

	return (
		<S.CodeBlockWrapper $isCollapsed={isCollapsed} $hasOverflow={hasOverflow}>
			<Tooltip placement="bottom" id={tooltipId} />
			<S.CodeActionsTop>
				<S.IconButton
					onClick={handleCopy}
					data-tooltip-id={tooltipId}
					data-tooltip-content="Copy to clipboard"
				>
					<Icon type={IconType.COPY} width={14} height={14} color="currentColor" />
				</S.IconButton>
			</S.CodeActionsTop>
			<pre>
				<code>{children}</code>
			</pre>
			{hasOverflow && (
				<S.ShowMoreLink onClick={() => setIsCollapsed(!isCollapsed)}>
					{isCollapsed ? 'Show more' : 'Show less'}
				</S.ShowMoreLink>
			)}
		</S.CodeBlockWrapper>
	);
});

CodeBlock.displayName = 'CodeBlock';

const UUID_REGEX = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

// Replace known frame UUIDs in text with markdown links using frame:// scheme
const replaceFrameIds = (text: string, frameIds?: Set<string>): string => {
	if (!frameIds || frameIds.size === 0) return text;
	return text.replace(UUID_REGEX, (match) => {
		const normalized = match.toLowerCase();
		if (frameIds.has(normalized)) {
			return `[${normalized}](frame://${normalized})`;
		}
		return match;
	});
};
// Convert bare URLs to markdown links and replace frame UUIDs, while protecting
// fenced code blocks, inline code, and existing markdown links from modification.
const linkifyAndReplaceFrames = (text: string, frameIds?: Set<string>): string => {
	const placeholders: string[] = [];
	const ph = (match: string) => {
		placeholders.push(match);
		return `__MD_PH_${placeholders.length - 1}__`;
	};
	// Protect fenced code blocks, inline code, existing markdown links, and <frames> blocks
	// (during streaming a <frames> block may be incomplete, so protect partial blocks too)
	let result = text
		.replace(/<frames>[\s\S]*?(<\/frames>|$)/g, ph)
		.replace(/```[\s\S]*?```/g, ph)
		.replace(/`[^`]+`/g, ph)
		.replace(/\[([^\]]*)\]\(([^)]*)\)/g, ph);
	// Linkify bare URLs, then replace frame UUIDs — both on unprotected text only
	result = result.replace(/(https?:\/\/[^\s<>\)\]]+?)(?=[,.!?;:]*(?:\s|$))/g, '[$1]($1)');
	result = replaceFrameIds(result, frameIds);
	return result.replace(/__MD_PH_(\d+)__/g, (_, i) => placeholders[parseInt(i)]);
};

// Memoized on children (the markdown string) to avoid re-parsing unchanged messages.
const MarkdownText = React.memo(
	({ children, frameIds, onContextMenu }: MarkdownTextProps) => {
		const isMobile = useSelector((r: RootReducer) => selectIsMobile(r.mode));

		// Convert bullet point characters to markdown format
		// Convert • to - for proper markdown bullet lists
		const processedContent = linkifyAndReplaceFrames(children.replace(/^• /gm, '- '), frameIds);

		// Memoize components to prevent CodeBlock from being recreated on every render
		const components = useMemo(
			() => ({
				code: ({ inline, children, ...props }: any) => {
					const codeString = String(children);
					// Only use CodeBlock for multi-line code blocks
					// Single-line code (inline or block) should render as simple inline code
					const isMultiLine = codeString.includes('\n');
					if (inline || !isMultiLine) {
						return <code {...props}>{children}</code>;
					}
					return <CodeBlock>{codeString}</CodeBlock>;
				},
				a: ({ href, children, ...props }: any) => {
					if (href && href.startsWith('frame://')) {
						const frameId = href.replace('frame://', '');
						return <FrameLink frameId={frameId} />;
					}
					return (
						<a {...props} href={href} target="_blank" rel="noopener noreferrer">
							{children}
						</a>
					);
				},
			}),
			[]
		);

		return (
			<S.MarkdownContent onContextMenu={onContextMenu} isMobile={isMobile}>
				<ReactMarkdown
					components={components}
					remarkPlugins={[remarkGfm]}
					urlTransform={(url) => (url.startsWith('frame://') ? url : defaultUrlTransform(url))}
				>
					{processedContent}
				</ReactMarkdown>
			</S.MarkdownContent>
		);
	},
	(prev, next) =>
		prev.children === next.children &&
		prev.frameIds === next.frameIds &&
		prev.onContextMenu === next.onContextMenu
);

MarkdownText.displayName = 'MarkdownText';

export default MarkdownText;
